import React, { useState, useEffect } from 'react';
import {
  Card, Form, Input, Button, message, Spin, Alert, Space, Cascader,
  Select, DatePicker, InputNumber, Divider, Typography, Row, Col, Tag
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { schoolAPI } from '../../services/api';
import dayjs from 'dayjs';
import { areaData } from '../../utils/areaData';
import { getAreaCodesByLabels, getAreaLabelsByCodes } from '../../utils/areaHelpers';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_OPTIONS = [
  { value: 'active',   label: '正常运营', color: 'success' },
  { value: 'trial',    label: '试用中',   color: 'processing' },
  { value: 'inactive', label: '已停用',   color: 'error' },
  { value: 'expired',  label: '已到期',   color: 'warning' },
];

const PLAN_OPTIONS = [
  { value: 'free',       label: '免费版' },
  { value: 'basic',      label: '基础版' },
  { value: 'pro',        label: '专业版' },
  { value: 'enterprise', label: '企业版' },
];

const SchoolEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [schoolName, setSchoolName] = useState('');

  useEffect(() => {
    fetchSchoolDetails();
  }, [id]);

  const fetchSchoolDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await schoolAPI.getById(id);
      if (response.data.success) {
        const d = response.data.data;
        setSchoolName(d.name);
        form.setFieldsValue({
          name:         d.name,
          area_codes:   getAreaCodesByLabels({ province: d.province, city: d.city, county: d.county }),
          address:      d.address || '',
          principal:    d.principal || '',
          phone:        d.phone || '',
          email:        d.email || '',
          status:       d.status || 'active',
          plan_type:    d.plan_type || 'free',
          expire_date:  d.expire_date ? dayjs(d.expire_date) : null,
          max_classes:  d.max_classes || 50,
          max_teachers: d.max_teachers || 200,
          notes:        d.notes || '',
        });
      } else {
        setError(response.data.message || '获取学校信息失败');
      }
    } catch (err) {
      setError('获取学校信息失败，请稍后再试');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setSaving(true);
      const { area_codes, ...restValues } = values;
      const { province, city, county } = getAreaLabelsByCodes(area_codes);
      const payload = {
        ...restValues,
        province: province || null,
        city: city || null,
        county: county || null,
        expire_date: restValues.expire_date ? restValues.expire_date.format('YYYY-MM-DD') : null,
      };
      const response = await schoolAPI.update(id, payload);
      if (response.data.success) {
        message.success('学校信息更新成功');
        navigate(`/schools/${id}`);
      } else {
        message.error(response.data.message || '更新失败');
      }
    } catch (err) {
      message.error(err.response?.data?.message || '更新学校信息失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      <Card
        title={
          <Space>
            <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/schools/${id}`)} style={{ padding: 0 }} />
            <Title level={4} style={{ margin: 0 }}>
              编辑学校 {schoolName && <Text type="secondary" style={{ fontSize: 14 }}>— {schoolName}</Text>}
            </Title>
          </Space>
        }
      >
        <Spin spinning={loading}>
          {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
          >
            <Divider orientation="left">基本信息</Divider>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="name" label="学校名称" rules={[{ required: true, message: '请输入学校名称' }]}>
                  <Input placeholder="请输入学校全称" maxLength={100} showCount />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="area_codes"
                  label="所在地区"
                  rules={[{ required: true, message: '请选择学校所在的省市县' }]}
                >
                  <Cascader options={areaData} placeholder="请选择省 / 市 / 县" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="address" label="详细地址">
                  <Input placeholder="请输入学校详细地址" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="principal" label="联系人">
                  <Input placeholder="联系人姓名" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="phone" label="联系电话">
                  <Input placeholder="联系电话" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="email" label="邮箱">
                  <Input placeholder="学校邮箱" />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left">服务配置</Divider>
            <Alert
              message="提示：状态变更建议在学校详情页的【状态管理】按钮操作，可同时设置到期日期。"
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="status" label="学校状态">
                  <Select>
                    {STATUS_OPTIONS.map(opt => (
                      <Option key={opt.value} value={opt.value}>
                        <Tag color={opt.color}>{opt.label}</Tag>
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="plan_type" label="套餐类型">
                  <Select>
                    {PLAN_OPTIONS.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="expire_date" label="到期日期（留空为永久）">
                  <DatePicker style={{ width: '100%' }} placeholder="选择到期日期" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="max_classes" label="最大班级数">
                  <InputNumber min={1} max={9999} style={{ width: '100%' }} addonAfter="个班" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="max_teachers" label="最大教师数">
                  <InputNumber min={1} max={99999} style={{ width: '100%' }} addonAfter="位教师" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="notes" label="管理员备注（仅管理员可见）">
              <TextArea rows={2} placeholder="可填写合同信息、沟通记录等" maxLength={500} showCount />
            </Form.Item>

            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />} size="large">
                  保存更改
                </Button>
                <Button size="large" onClick={() => navigate(`/schools/${id}`)}>
                  取消
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Card>
    </Space>
  );
};

export default SchoolEdit;
